/**
 * Build 6 — Personal investigation workspace.
 *
 * Six restrained sections in priority order. No badges, no streaks, no
 * notifications, no feed. Modular: each section is a small component
 * that reads from existing APIs (Builds 1–5).
 *
 * Empty states are invitations to investigate — never SaaS hand-holding.
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useMembership } from "../lib/membership";
import Header from "../components/Header";
import SubscriptionStatusBanner from "../components/SubscriptionStatusBanner";
import LogoMark from "../components/LogoMark";
import api from "../lib/api";
import { toast } from "sonner";
import {
    ArrowRight, BookOpen, Bookmark, Compass, ExternalLink,
    LogOut, Search, Sparkles, ShieldCheck,
} from "lucide-react";

const MEMBERSHIP_URL = process.env.REACT_APP_MEMBERSHIP_URL
    || "/membership";
const MANAGE_MEMBERSHIP_URL = process.env.REACT_APP_MANAGE_MEMBERSHIP_URL || "https://thecontextproject.com/account";

const PLAN_LABELS = {
    monthly: "Monthly · £17/mo",
    yearly: "Yearly · £177/yr",
    lifetime: "Lifetime · £377",
};

const QUIZ_DISMISSED_KEY = "tcp:onboarding_dismissed";

// ---- thin data hooks (no caching layer; React handles refetch on mount) ----
function useWorkspaceData() {
    const [cont, setCont] = useState(null);
    const [sugg, setSugg] = useState(null);
    const [active, setActive] = useState(null);
    const [marks, setMarks] = useState(null);

    useEffect(() => {
        let cancelled = false;
        Promise.all([
            api.get("/me/continue").catch(() => ({ data: [] })),
            api.get("/me/suggested").catch(() => ({ data: [] })),
            api.get("/active-investigations").catch(() => ({ data: [] })),
            api.get("/me/bookmarks").catch(() => ({ data: [] })),
        ]).then(([c, s, a, b]) => {
            if (cancelled) return;
            setCont(c.data || []);
            setSugg(s.data || []);
            setActive(a.data || []);
            setMarks(b.data || []);
        });
        return () => { cancelled = true; };
    }, []);

    return { cont, sugg, active, marks };
}

// ---- section primitives ----
const SectionHeading = ({ eyebrow, title }) => (
    <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#56CCF2] font-medium mb-2">{eyebrow}</div>
        <h2 className="text-xl sm:text-2xl font-semibold text-white tracking-tight">{title}</h2>
    </div>
);

const RestrainedCard = ({ to, onClick, eyebrow, title, subtitle, foot, dataTestId }) => (
    <Link
        to={to}
        onClick={onClick}
        data-testid={dataTestId}
        className="group block px-5 py-5 rounded-xl bg-[#0B1220] border border-[#243044] hover:border-[#2F80ED] hover:bg-[#111827] transition-all"
    >
        {eyebrow ? (
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#56CCF2] font-medium mb-3">{eyebrow}</div>
        ) : null}
        <h3 className="text-base font-semibold text-white leading-snug mb-1 group-hover:text-[#56CCF2] transition-colors">{title}</h3>
        {subtitle ? (
            <p className="text-[13px] leading-[1.6] text-[#94A3B8] line-clamp-2">{subtitle}</p>
        ) : null}
        {foot ? <div className="mt-4 text-xs text-[#64748B]">{foot}</div> : null}
    </Link>
);

// ---- sections ----
function ContinueInvestigating({ items }) {
    if (items === null) return null;
    if (items.length === 0) {
        return (
            <section>
                <SectionHeading eyebrow="Continue" title="Pick up where you left off" />
                <Link
                    to="/"
                    className="block px-5 py-6 rounded-xl bg-[#0B1220] border border-dashed border-[#243044] hover:border-[#2F80ED] transition-colors"
                    data-testid="continue-empty"
                >
                    <p className="text-[15px] text-[#CBD5E1] mb-1">There's a library waiting.</p>
                    <p className="text-[13px] text-[#64748B]">Open an investigation and we'll remember where you were.</p>
                </Link>
            </section>
        );
    }
    return (
        <section>
            <SectionHeading eyebrow="Continue" title="Pick up where you left off" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="continue-list">
                {items.map((t) => (
                    <RestrainedCard
                        key={t.slug}
                        to={`/topic/${t.slug}`}
                        dataTestId={`continue-${t.slug}`}
                        eyebrow={t.category || "Investigation"}
                        title={t.title}
                        subtitle={t.subtitle}
                        foot={
                            <div className="flex items-center gap-3 text-[11px] text-[#64748B]">
                                {typeof t.progress_pct === "number" && t.progress_pct > 0 ? (
                                    <span>{t.progress_pct}% explored</span>
                                ) : null}
                                {t.connected_threads ? (
                                    <span>· {t.connected_threads} connected threads</span>
                                ) : null}
                            </div>
                        }
                    />
                ))}
            </div>
        </section>
    );
}

function SuggestedThreads({ items }) {
    if (items === null) return null;
    if (items.length === 0) {
        return (
            <section>
                <SectionHeading eyebrow="Threads ahead" title="Adjacent systems" />
                <p className="text-[13px] text-[#64748B] max-w-xl">
                    As you bookmark and explore, we'll surface the connected investigations here.
                </p>
            </section>
        );
    }
    return (
        <section>
            <SectionHeading eyebrow="Threads ahead" title="Adjacent systems worth exploring" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="suggested-list">
                {items.map((t) => (
                    <RestrainedCard
                        key={t.slug}
                        to={`/topic/${t.slug}`}
                        dataTestId={`suggested-${t.slug}`}
                        eyebrow={t.category}
                        title={t.title}
                        subtitle={t.subtitle}
                        foot={<span className="inline-flex items-center gap-1.5 text-[#94A3B8] group-hover:text-[#56CCF2]"><Compass className="w-3.5 h-3.5" /> Follow the thread</span>}
                    />
                ))}
            </div>
        </section>
    );
}

function ActiveInvestigations({ items }) {
    if (items === null) return null;
    if (items.length === 0) return null;
    return (
        <section>
            <SectionHeading eyebrow="In the archive" title="Recently in motion" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="active-list">
                {items.map((t) => (
                    <RestrainedCard
                        key={t.slug}
                        to={`/topic/${t.slug}`}
                        dataTestId={`active-${t.slug}`}
                        eyebrow={t.editorial_status_label || "Update"}
                        title={t.title}
                        subtitle={t.subtitle}
                    />
                ))}
            </div>
        </section>
    );
}

function ResearchTrails({ items }) {
    if (items === null) return null;
    // Group bookmarks by parent_ref. Items without parent_ref but whose own
    // kind is research-graph (vaccine / vaccine_comparison / manufacturer /
    // ingredient / disease / surveillance_system) become their own trail
    // root so members see them grouped under that page's title.
    const RESEARCH_KINDS = new Set([
        "vaccine", "vaccine_schedule", "vaccine_age_cohort", "vaccine_comparison",
        "evidence_block", "research_area", "ingredient", "disease",
        "manufacturer", "surveillance_system",
    ]);
    const trails = {};
    let anyResearch = false;
    for (const b of items) {
        const isResearch = RESEARCH_KINDS.has(b.kind) || (b.parent_ref || "").length > 0;
        if (!isResearch) continue;
        anyResearch = true;
        const trailKey = b.parent_ref || `${b.kind}:${b.ref_slug}`;
        trails[trailKey] = trails[trailKey] || { key: trailKey, root: null, items: [] };
        if (!b.parent_ref && RESEARCH_KINDS.has(b.kind)) trails[trailKey].root = b;
        else trails[trailKey].items.push(b);
    }
    if (!anyResearch) return null;
    return (
        <section data-testid="research-trails">
            <SectionHeading eyebrow="My research" title="Your research trails" />
            <div className="space-y-5">
                {Object.values(trails).map((t) => {
                    // Trail title: root bookmark title if present, otherwise
                    // pretty-format the parent_ref ("vaccine:mmr" → "MMR trail").
                    const rootTitle = t.root?.ref_title
                        || (t.key.includes(":") ? `${t.key.split(":")[1].toUpperCase()} trail` : t.key);
                    const allItems = t.root ? [t.root, ...t.items] : t.items;
                    return (
                        <div key={t.key} className="bg-[#0B1220] border border-[#243044] rounded-lg p-4" data-testid={`trail-${t.key}`}>
                            <div className="text-[11px] uppercase tracking-[0.22em] text-[#56CCF2] font-semibold mb-2.5">{rootTitle}</div>
                            <ul className="space-y-1.5">
                                {allItems.map((b) => {
                                    const href = b.ref_url
                                        || (b.kind === "vaccine" ? `/vaccines/v/${b.ref_slug}`
                                            : ["ingredient", "disease", "manufacturer", "surveillance_system"].includes(b.kind) ? `/entity/${b.ref_slug}`
                                            : b.kind === "vaccine_comparison" ? `/vaccines/compare/${b.ref_slug}`
                                            : b.kind === "topic" ? `/topic/${b.ref_slug}`
                                            : b.kind === "source" ? `/source/${b.ref_slug}`
                                            : b.kind === "entity" ? `/entity/${b.ref_slug}`
                                            : "#");
                                    return (
                                        <li key={b.id}>
                                            <Link to={href} className="text-[13px] text-[#CBD5E1] hover:text-white inline-flex items-center gap-2">
                                                <Bookmark className="w-3 h-3 text-[#56CCF2]" />
                                                <span>{b.ref_title}</span>
                                                <span className="text-[9px] uppercase tracking-wider text-[#64748B] ml-1">{(b.kind || "").replace("_", " ")}</span>
                                            </Link>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}

function SavedInvestigations({ items }) {
    if (items === null) return null;
    if (items.length === 0) {
        return (
            <section>
                <SectionHeading eyebrow="Saved" title="Your investigations" />
                <p className="text-[13px] text-[#64748B] max-w-xl">
                    Bookmark anything that pulls at you. Your saved threads are your map.
                </p>
            </section>
        );
    }
    // Cluster grouping — by source_topic_cluster, falling back to "Other".
    const groups = {};
    for (const b of items.slice(0, 24)) {
        const k = b.cluster || "Other";
        groups[k] = groups[k] || [];
        groups[k].push(b);
    }
    return (
        <section>
            <SectionHeading eyebrow="Saved" title="Your investigations" />
            <div className="space-y-6" data-testid="saved-list">
                {Object.entries(groups).map(([cluster, list]) => (
                    <div key={cluster}>
                        <div className="text-[11px] uppercase tracking-[0.18em] text-[#94A3B8] mb-2">{cluster}</div>
                        <ul className="space-y-1.5">
                            {list.map((b) => (
                                <li key={b.id || b._server_id || b.ref_slug}>
                                    <Link
                                        to={`/topic/${b.id}`}
                                        className="text-[14px] text-[#CBD5E1] hover:text-white inline-flex items-center gap-2"
                                    >
                                        <Bookmark className="w-3 h-3 text-[#56CCF2]" />
                                        {b.title}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
            {items.length > 24 ? (
                <Link
                    to="/saved"
                    className="mt-6 inline-flex items-center gap-1.5 text-xs text-[#94A3B8] hover:text-[#56CCF2]"
                >
                    View all saved <ArrowRight className="w-3.5 h-3.5" />
                </Link>
            ) : null}
        </section>
    );
}

function SearchPanel() {
    const [q, setQ] = useState("");
    const navigate = useNavigate();
    const onSubmit = (e) => {
        e.preventDefault();
        const term = q.trim();
        if (!term) return;
        navigate(`/search?q=${encodeURIComponent(term)}`);
    };
    return (
        <section>
            <SectionHeading eyebrow="Search" title="What are you curious about?" />
            <form onSubmit={onSubmit} className="relative max-w-2xl" data-testid="workspace-search">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                <input
                    type="text"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Try: BlackRock · GLP-1 · data centres · 15-minute cities"
                    className="w-full pl-11 pr-4 py-3 rounded-full bg-[#0B1220] border border-[#243044] focus:border-[#56CCF2] focus:outline-none text-[15px] text-white placeholder:text-[#64748B]"
                />
            </form>
        </section>
    );
}

function VaccineExplorerSuggestion({ user }) {
    // Surface a quiet "Suggested for your research interests" prompt for
    // members whose onboarding quiz answers indicate vaccine / medical
    // research interest. Quiet by design — never blocks the dashboard.
    const interests = user?.preferences?.interests || [];
    const SIGNALS = ["vaccines", "vaccination", "childrens-health", "medical-research", "pharmaceutical", "health"];
    const matches = interests.filter((i) => SIGNALS.includes((i || "").toLowerCase()));
    if (matches.length === 0) return null;
    return (
        <section className="border-t border-[#243044] pt-10" data-testid="vaccine-suggestion">
            <SectionHeading eyebrow="Suggested for you" title="Vaccination Research Explorer" />
            <p className="text-[13px] text-[#94A3B8] max-w-2xl mb-5">
                Based on your research interests, the Vaccination Research Explorer pairs official UK and US schedules with structured evidence, surveillance data and TCP investigations.
            </p>
            <div className="flex flex-wrap gap-2">
                <Link to="/vaccines" data-testid="suggestion-hub" className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md bg-[#2F80ED] hover:bg-[#1E6FE0] text-white text-xs font-medium transition-colors">
                    Open the Explorer <ArrowRight className="w-3.5 h-3.5" />
                </Link>
                <Link to="/vaccines/uk" data-testid="suggestion-uk" className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md bg-[#111827] hover:bg-[#1a2233] text-[#CBD5E1] border border-[#243044] text-xs font-medium transition-colors">
                    UK schedule
                </Link>
                <Link to="/vaccines/us" data-testid="suggestion-us" className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md bg-[#111827] hover:bg-[#1a2233] text-[#CBD5E1] border border-[#243044] text-xs font-medium transition-colors">
                    US schedule
                </Link>
                <Link to="/vaccines/compare/uk-vs-us" data-testid="suggestion-compare" className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md bg-[#111827] hover:bg-[#1a2233] text-[#CBD5E1] border border-[#243044] text-xs font-medium transition-colors">
                    UK vs US comparison
                </Link>
            </div>
        </section>
    );
}

function RefinePathway({ user }) {
    const prefs = user?.preferences || {};
    const region = prefs.region || "global";
    const depth = prefs.depth_mode || "beginner";
    const interests = prefs.interests || [];
    return (
        <section className="border-t border-[#243044] pt-10">
            <SectionHeading eyebrow="Your pathway" title="Refine your focus" />
            <div className="flex flex-wrap gap-x-6 gap-y-3 text-[13px] text-[#94A3B8] mb-5">
                <span>Region · <span className="text-[#CBD5E1] uppercase tracking-wider">{region}</span></span>
                <span>Depth · <span className="text-[#CBD5E1]">{depth === "deep" ? "deep" : "calm overview"}</span></span>
                {interests.length > 0 ? (
                    <span>Threads · <span className="text-[#CBD5E1]">{interests.length} selected</span></span>
                ) : null}
            </div>
            <Link
                to="/welcome"
                onClick={() => { try { localStorage.removeItem(QUIZ_DISMISSED_KEY); } catch { /* ignore */ } }}
                data-testid="refine-pathway-link"
                className="inline-flex items-center gap-2 text-sm text-[#56CCF2] hover:text-white"
            >
                Refine your pathway <ArrowRight className="w-4 h-4" />
            </Link>
        </section>
    );
}

// ---- page ----
export default function DashboardPage() {
    const { user, refresh, checking } = useAuth();
    const { isAuthMember } = useMembership();
    const navigate = useNavigate();
    const { cont, sugg, active, marks } = useWorkspaceData();

    // Note: we deliberately do NOT auto-redirect logged-in members to the
    // onboarding quiz. Seeded / returning members would otherwise be pushed
    // into the quiz on every login. Users can take the quiz at any time via
    // the in-dashboard prompt (and refining via /workspace settings).

    if (checking) {
        return (
            <div className="min-h-screen bg-[#0A0F1A] text-[#94A3B8] flex items-center justify-center text-sm">
                Loading your archive…
            </div>
        );
    }
    if (!user) {
        return (
            <div className="min-h-screen bg-[#0A0F1A] text-[#94A3B8] flex flex-col items-center justify-center px-6 text-center">
                <div className="mb-8">
                    <LogoMark size="md" dataTestId="dashboard-signin-logo" />
                </div>
                <p className="text-sm mb-4">You need to sign in to access your workspace.</p>
                <Link to="/account/login?next=/dashboard" className="text-[#56CCF2] hover:text-white inline-flex items-center gap-2" data-testid="dashboard-redirect-login">
                    Sign in <ArrowRight className="w-4 h-4" />
                </Link>
            </div>
        );
    }

    const planLabel = PLAN_LABELS[user.membership_plan] || (isAuthMember ? "Active member" : null);
    const isAdmin = user.role === "admin";

    const signOut = async () => {
        try { await api.post("/customer/logout"); } catch { /* ignore */ }
        try { await api.post("/auth/logout"); } catch { /* ignore */ }
        await refresh();
        toast.message("Signed out.");
        navigate("/");
    };

    return (
        <div className="min-h-screen bg-[#0A0F1A] text-white">
            <Header />
            <main className="max-w-5xl mx-auto px-6 sm:px-8 py-12 sm:py-16 space-y-14">
                {/* Subscription state grace banner — silent for active/null */}
                <SubscriptionStatusBanner user={user} />

                {/* Section 1 — Continue investigating */}
                <ContinueInvestigating items={cont} />

                {/* Section 2 — Suggested threads */}
                <SuggestedThreads items={sugg} />

                {/* Section 3 — Active investigations (editorial) */}
                <ActiveInvestigations items={active} />

                {/* Section 4 — Saved */}
                <SavedInvestigations items={marks} />
                <ResearchTrails items={marks} />

                {/* Section 5 — Search */}
                <SearchPanel />

                {/* Section 6 — Refine your pathway */}
                <VaccineExplorerSuggestion user={user} />
                <RefinePathway user={user} />

                {/* Quiet footer — membership + sign-out */}
                <section className="border-t border-[#243044] pt-10 flex flex-wrap items-center justify-between gap-4">
                    <div className="text-[13px] text-[#64748B]">
                        Signed in as <span className="text-[#CBD5E1]">{user.email}</span>
                        {planLabel ? <span className="ml-3 text-[#94A3B8]">· {planLabel}</span> : null}
                        {isAdmin ? (
                            <span className="ml-3 inline-flex items-center gap-1.5 text-[#56CCF2]">
                                <ShieldCheck className="w-3 h-3" /> admin
                            </span>
                        ) : null}
                    </div>
                    <div className="flex items-center gap-4">
                        {isAuthMember ? (
                            <a
                                href={MANAGE_MEMBERSHIP_URL}
                                target="_blank" rel="noreferrer"
                                data-testid="manage-membership"
                                className="text-sm text-[#94A3B8] hover:text-[#56CCF2] inline-flex items-center gap-1.5"
                            >
                                Manage membership <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                        ) : (
                            <a
                                href={MEMBERSHIP_URL}
                                data-testid="become-member"
                                className="text-sm text-[#56CCF2] hover:text-white inline-flex items-center gap-1.5"
                            >
                                Become a member <ArrowRight className="w-3.5 h-3.5" />
                            </a>
                        )}
                        <button
                            onClick={signOut}
                            data-testid="dashboard-signout"
                            className="text-sm text-[#64748B] hover:text-[#94A3B8] inline-flex items-center gap-1.5"
                        >
                            <LogOut className="w-3.5 h-3.5" /> Sign out
                        </button>
                    </div>
                </section>
            </main>
        </div>
    );
}
